// © Microsoft Corporation. All rights reserved.
import { __awaiter } from "tslib";
import { getTargetScope } from "./Variables";
import { environment, logger, perfMarker, ExperienceTypes, IndexedDb, RiverConfigType, registerCommandMessageHandler, WebWorkerMessageId, registerDataMessageHandler, sendMessage } from "../wwCommon";
import { get, isArray, isEmpty, isString, mergeWith, isEqual } from "lodash-es";
import { stringHasData } from "./Utility";
import { getPeregrineFlights } from "@msnews/experimentation-helpers";
import { getLiveCacheBustingIdentifier } from "./ConfigCacheBustHelper";
import { initializeGitResolverEnvironment, getGitConfigTree, updateConfigsCache, experienceGitConfigMap } from "./wwGitConfigResolver";
import { HrefNotValid, GetConfigTreeError, GetConfigTreeEmpty, GetConfigTreeNull, PageScopeInvalid, CrsFetchError, ErrorParsingConfigTree, IdbConfigReadFailed, IdbConfigWriteFailed, IdbConfigInitFailed, IdbConfigClearFailed, RefreshConfigsNewConfigsFailed, RefreshConfigsCbidError, RefreshConfigsError, ConfigCrsFetchNon200Response, ConfigCdnFetchNon200Response, ConfigsDownloadedByCdn } from "../AppErrors";
import { SubscribableMap } from "../wwCommon/SubscribableMap";
/** Dictionary to store configs needed per experience type */
export const experienceConfigMap = new SubscribableMap();
/** Registers config resolver realted tasks */
export function registerConfigResolverTasks() {
    if (environment.disableWWConfigPreload) {
        return;
    }
    // initialize config resolver base URL & params
    initializeWwResolver();
    registerCommandMessageHandler(WebWorkerMessageId.ClearConfigCache, clearConfigCache);
    registerDataMessageHandler(WebWorkerMessageId.RefreshConfigs, () => {
        RefreshConfigs().then((response) => {
            sendMessage({ id: WebWorkerMessageId.RefreshConfigs, fetched: true, payload: response });
        });
    });
}
/**
 * Target scope of the current page request
 * @type {PageRequestTargetScope}
 */
let currentTargetScope;
/**
 * Target scope of the current page request with only Peregrine scoped flights
 * @type {PageRequestTargetScope}
 */
let scopeWithPeregrineFlights;
/**
 * Dictionary to maintain all resolved config for internal state management
 * @type {Map<string, ExperienceConfigWrapper>}
 */
const configMap = new Map();
// set fallback env to prod to avoid any live site issues
/**
 * Config base url used during cms config resolution.
 * @type {string}
 */
let cdnConfigBaseUrl;
/**
 * Config base url for Config Service
 * @type {string}
 */
let configServiceBaseUrl;
/**
 * Promise map of all file download promises, so that we don't accidentally make duplicate calls
 */
const donwloadPromiseMap = new Map();
const downloadMap = new Map();
/**
 * Dictionary of config index hrefs those are already visited.
 */
let visitedHrefs = {};
/* Check if ConfigService operation is allowed or not.
* @type {boolean}
*/
let enableConfigService = false;
/* Get config service parameters from currentTargetScope
* @type {string}
*/
let configServiceParameters = "";
/**
 * Instance of IndexedDb class
 */
let indexedDb;
/* Check if IndexedDb operation is allowed or not.
* @type {boolean}
*/
let useIndexedDb;
/**
 * Current Timestamp for config staleness check
 */
const stalenessCheckTimestamp = new Date().getTime();
/** Set to true when configs started to write to indexedDb. */
let writeConfigsToIndexedDbCalled;
/** Promise map of config tree download promise. */
const configTreePromiseMap = new Map();
/**
 * Check if config service should return more than 1 config
 * @type {boolean}
 */
let isMultiConfigRetrievalEnabled = false;
/**
 * Experience configs needed as soon as possible
 */
const experienceConfigsNeeded = [
    // DO NOT change the order of entrypoint configs, they should added to the Map first
    ExperienceTypes.EntryPointEdgeNext,
    ExperienceTypes.EntryPoint,
    // Order doesn't matter for the below configs
    ExperienceTypes.River,
    ExperienceTypes.WeatherCard,
    ExperienceTypes.PlacementManager,
    ExperienceTypes.MyBriefing,
    ExperienceTypes.NewsbarFeed,
    ExperienceTypes.DesktopFeed,
    ExperienceTypes.DesktopFeedEdgeNext,
    ExperienceTypes.DesktopFeedSingleColumn,
    ExperienceTypes.IndustryNewsCard
];
/**
 * Initialize web worker resolver.
 * Export is for test purposes only.
 */
export function initializeWwResolver(idb) {
    if (!environment.env) {
        return;
    }
    cdnConfigBaseUrl = environment.configRootUrl;
    currentTargetScope = getTargetScope();
    if (currentTargetScope) {
        scopeWithPeregrineFlights = Object.assign(Object.assign({}, currentTargetScope), { pageExperiments: getPeregrineFlights(currentTargetScope.pageExperiments) });
    }
    indexedDb = idb ? idb : new IndexedDb();
    // Check if IndexedDb is supported
    useIndexedDb = indexedDb.supported
        // PCS flag to turn on/off IndexedDb caching of configs.
        // Flag to turn on/off IndexedDb caching for debugging purposes
        && indexedDb.isCachingAllowed
        // Use case is simple config tests, we do not want to cache
        // Also not caching in server-side in this scenario
        && !environment.enableCMSPPEConfigs
        // TODO: If this is GitConfigs flight, do not use indexedDB for now to avoid getting unnecessary bugs.
        // Once IndexedDB is fixed for Git configs, this line will be removed
        && !environment.isGitConfigs;
    enableConfigService = useConfigService();
    if (enableConfigService) {
        formConfigServiceUrl(environment.cacheBustingIdentifier);
    }
    if (environment.isGitConfigs) {
        initializeGitResolverEnvironment(scopeWithPeregrineFlights);
    }
}
/**
 * Form config service url
 * @param cacheBustingIdentifier cbid to append in query parameter.
 */
function formConfigServiceUrl(cacheBustingIdentifier) {
    configServiceBaseUrl = environment.configServiceBaseUrl;
    // Query string parameter from target scope.
    configServiceParameters = "?targetScope=" + JSON.stringify(scopeWithPeregrineFlights);
    // Send PCS apptype to CRS so we can build CRS Jarvis dashboards per apptype.
    configServiceParameters += `&apptype=${environment.pcsApptype}`;
    // Check if CRS should fetch multiple configs and its depth.
    isMultiConfigRetrievalEnabled = environment.multiConfigRetrievalEnabled;
    if (isMultiConfigRetrievalEnabled) {
        configServiceParameters += `&maxDepth=${getConfigRetrievalMaxDepth()}`;
    }
    // Send cacheBustingIdentifier to CRS
    if (cacheBustingIdentifier) {
        configServiceParameters += `&cbid=${cacheBustingIdentifier}`;
    }
    // If url has dev only query string params, inform CRS so it can exclude from availability and will help debugging on Jarvis.
    if (containsDevOnlyQueryParameters()) {
        configServiceParameters += "&item=dev:true";
    }
    if (environment.enableCMSPPEConfigs) {
        configServiceParameters += "&item=cms:ppe";
    }
}
/**
 * This function generates a key that is used to write/read configurations to/from storage.
 * example key: "kids|ja|jp|testPage|mmx"
 * @private
 * @return string
 */
export function generateKey() {
    const { audienceMode = "", locale = {}, pageType = "" } = currentTargetScope || {};
    const { language, market } = locale;
    const keys = [audienceMode, language, market, pageType, environment.pcsApptype || ""];
    return keys.map(key => key || "").join("|").toLowerCase();
}
/**
 * Start downloading Config tree.
 * @param: cache busting identifier to be used in CRS network call as query string param.
 */
export function loadConfigTree(liveCacheBustingIdentifier) {
    return __awaiter(this, void 0, void 0, function* () {
        const cbid = liveCacheBustingIdentifier || environment.cacheBustingIdentifier || "";
        const configTreeTraversalPromiseKey = `loadConfigTreeCbid-${(cbid)}`;
        // if configTreePromiseMap already has this item, we have config tree download in progress, return this promise.
        if (configTreePromiseMap.has(configTreeTraversalPromiseKey)) {
            logger.info(`WebWorker.loadConfigs: promise was already resolved and configMap has ${configMap.size} configs.`);
            return configTreePromiseMap.get(configTreeTraversalPromiseKey);
        }
        perfMarker("loadConfigs-start");
        const configTreePromise = new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            try {
                perfMarker(`getConfigTree-${(cbid)}-start`);
                logger.info(`WebWorker.loadConfigs: loading configs over the network for cbid: ${cbid}.`);
                if (environment.isGitConfigs) {
                    const configRef = {
                        experienceType: "AppConfig",
                        instanceSrc: "default"
                    };
                    yield getGitConfigTree(configRef);
                    addConfigToExperienceConfigMap();
                }
                else {
                    yield getConfigTree(environment.configIndexDocId);
                }
                logger.info(`WebWorker.loadConfigs: completed fetching ${configMap.size} configs over the network.`);
                resolve();
            }
            catch (e) {
                logger.error(Object.assign(Object.assign({}, ErrorParsingConfigTree), { message: "Error parsing config tree", pb: Object.assign(Object.assign({}, ErrorParsingConfigTree.pb), { customMessage: `Error: ${e}` }) }));
                reject(e);
            }
            finally {
                perfMarker(`getConfigTree-${(cbid)}-end`);
            }
        }));
        configTreePromiseMap.set(configTreeTraversalPromiseKey, configTreePromise);
        perfMarker("loadConfigs-end");
        return configTreePromise;
    });
}
/**
 * Get cached Configs from IndexedDb and populate configMap based on target scope key.
 */
function populateConfigsFromIndexedDb(indexedDbMap) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!useIndexedDb) {
            return;
        }
        try {
            const cachedConfigs = yield indexedDb.getObject(generateKey());
            if (!cachedConfigs) {
                logger.info("WebWorker.populateConfigsFromIndexedDb: Bulk read of configs didn't find any config.");
                return;
            }
            // targetScopeKey is inserted into IndexedDb along with configIndexHref.
            // Although there can be more than type of keys being formed since DHP and NTP will have different ones due to pageType in targetScope.
            cachedConfigs.forEach(cachedConfig => {
                if (cachedConfig.configIndexHref
                    && !isConfigStale(cachedConfig.storageTimestamp)) {
                    indexedDbMap.set(cachedConfig.configIndexHref, cachedConfig);
                }
            });
            logger.info(`WebWorker.populateConfigsFromIndexedDb completed. Found ${cachedConfigs.size} configs in Idb.`);
        }
        catch (e) {
            logger.error(Object.assign(Object.assign({}, IdbConfigReadFailed), { message: "IndexedDb Configs read failed", pb: Object.assign(Object.assign({}, IdbConfigReadFailed.pb), { customMessage: `Error: ${e}` }) }));
            // Once we get an exception using IndexedDb, stop trying to use it.
            useIndexedDb = false;
        }
    });
}
/**
 * Write configMap to indexedDb.
 */
function writeConfigMapToIndexedDb() {
    if (!useIndexedDb) {
        return;
    }
    try {
        // Write config tree to indexedDb as a single row.
        indexedDb.setObject(generateKey(), configMap).then(() => {
            logger.info("WebWorker.writeConfigMapToIndexedDb: completed");
        });
    }
    catch (e) {
        logger.error(Object.assign(Object.assign({}, IdbConfigWriteFailed), { message: "Failed to write configMap to indexedDb", pb: Object.assign(Object.assign({}, IdbConfigWriteFailed.pb), { customMessage: `Error: ${e}` }) }));
        // Once we get an exception using IndexedDb, stop trying to use it.
        useIndexedDb = false;
    }
}
/**
 * Read configs from IndexedDb as indexedDbMap
 * Compare indexedDbMap again configs received through network call aka configMap
 * Write configMap to indexedDb.
 * @returns RefreshConfigsResponse
 */
export function RefreshConfigs() {
    return __awaiter(this, void 0, void 0, function* () {
        const configsRefreshResponse = {
            configsEqual: true,
            liveCbId: undefined
        };
        if (environment.isGitConfigs) {
            configsRefreshResponse.configsEqual = yield updateConfigsCache();
            return configsRefreshResponse;
        }
        if (!useIndexedDb) {
            return configsRefreshResponse;
        }
        try {
            // If config tree wasn't loaded from network or still loading, let it complete first.
            yield loadConfigTree();
            // Check configMap size after loading config tree.
            // if configMap does not contain any config, do not proceed.
            if (configMap.size === 0) {
                return configsRefreshResponse;
            }
            // Populate configs from indexedDB.
            const indexedDbMap = new Map();
            const indexedDbPromise = new Promise((resolve) => __awaiter(this, void 0, void 0, function* () {
                // Initialize indexedDb.
                yield initializeIndexedDb();
                // Populate configs from indexedDb to indexedDbMap.
                yield populateConfigsFromIndexedDb(indexedDbMap);
                resolve();
            }));
            // Get live cache busting identifier
            let liveCacheBustingIdentifier;
            const cbIdPromise = new Promise((resolve) => __awaiter(this, void 0, void 0, function* () {
                liveCacheBustingIdentifier = yield getLiveCacheBustingIdentifier();
                resolve();
            }));
            // Make parallel calls read all configs from indexedDb and to read CBID from CRS.
            yield Promise.all([cbIdPromise, indexedDbPromise]);
            configsRefreshResponse.liveCbId = liveCacheBustingIdentifier;
            // If indexedDb has entrypoint config cached, get cbId from entrypoint config.
            // If either entrypoint config is not present in cache or cbId is not yet present in this config, no action.
            // CBID is a number starting from 1. It would not be less than 1 ever since we always increase it when required.
            // If live CBID <= cached CBID, no action!
            // If live CBID > cached CBID, clear all cached configs and refill indexedDB with new set of configs.
            if (liveCacheBustingIdentifier
                && indexedDbMap.size > 0
                && indexedDbMap.has(environment.configIndexDocId)) {
                const cachedEntrypointConfig = indexedDbMap.get(environment.configIndexDocId);
                const indexedDbCbid = cachedEntrypointConfig ? cachedEntrypointConfig.cbId : undefined;
                if (indexedDbCbid) {
                    if (!environment.isGitConfigs
                        && (indexedDbCbid !== liveCacheBustingIdentifier
                            || environment.cacheBustingIdentifier !== indexedDbCbid.toString())) {
                        logger.error(Object.assign(Object.assign({}, RefreshConfigsCbidError), { message: "CBID change detected", pb: Object.assign(Object.assign({}, RefreshConfigsCbidError.pb), { customMessage: `HighestCbid = ${environment.cacheBustingIdentifier || ""}. IndexedDbCbid = ${indexedDbCbid}. LiveCbid = ${liveCacheBustingIdentifier}` }) }));
                    }
                    if (liveCacheBustingIdentifier > indexedDbCbid) {
                        // Clear all entry from this indexedDb.
                        yield clearIndexedDb();
                        logger.info(`WebWorker.refreshConfigs: LiveCBID = ${liveCacheBustingIdentifier} > indexedDB CBID = ${indexedDbCbid} so IndexedDb configs are purged and starting to download fresh configs.`);
                        // Clear maps to make sure next set of config calls call pass through.
                        configMap.clear();
                        indexedDbMap.clear();
                        donwloadPromiseMap.clear();
                        downloadMap.clear();
                        visitedHrefs = {};
                        // Change CRS url to include new CBID and load config tree with this CRS url.
                        formConfigServiceUrl(liveCacheBustingIdentifier.toString());
                        yield loadConfigTree(liveCacheBustingIdentifier.toString());
                        if (configMap.size > 0) {
                            logger.info(`WebWorker.refreshConfigs: New configs are downloaded for CBID:${liveCacheBustingIdentifier}.`);
                        }
                        else {
                            if (!environment.isGitConfigs) {
                                logger.error(Object.assign(Object.assign({}, RefreshConfigsNewConfigsFailed), { message: "New configs were not downloaded for a CBID", pb: Object.assign(Object.assign({}, RefreshConfigsNewConfigsFailed.pb), { customMessage: liveCacheBustingIdentifier.toString() }) }));
                            }
                        }
                        // When CBID change is detected and we purged configs in indexedDb, set configsEqual to false
                        // So render cache can be purged as well in main thread.
                        configsRefreshResponse.configsEqual = false;
                    }
                }
            }
            // This comparison of indexedDb config and network config is for Render Cache feature.
            // IndexedDbMap is typically empty in FRE.
            // no need to proceed if empty.
            if (indexedDbMap.size > 0) {
                configMap.forEach(networkConfig => {
                    if (configsRefreshResponse.configsEqual && indexedDbMap.has(networkConfig.configIndexHref)) {
                        const cachedConfig = indexedDbMap.get(networkConfig.configIndexHref);
                        // For render cache
                        // Check if cached config and network configs are same
                        if (!areConfigsEqual(cachedConfig, networkConfig)) {
                            logger.info(`WebWorker.compareConfigs: ${networkConfig.experienceType || ""} config changed with configIndexHref: ${networkConfig.configIndexHref || ""}.`);
                            configsRefreshResponse.configsEqual = false;
                        }
                    }
                });
                logger.info(`WebWorker.refreshConfigs: Network configs and cached configs same: ${configsRefreshResponse.configsEqual}`);
            }
            // Write CBID to Entrypoint config object.
            // This way new CBID will be present in indexedDB for next time!
            if (liveCacheBustingIdentifier
                && configMap.has(environment.configIndexDocId)) {
                const entrypointConfig = configMap.get(environment.configIndexDocId);
                entrypointConfig.cbId = liveCacheBustingIdentifier;
            }
            // Write network configs to indexedDb.
            writeConfigMapToIndexedDb();
            writeConfigsToIndexedDbCalled = true;
        }
        catch (e) {
            logger.error(Object.assign(Object.assign({}, RefreshConfigsError), { message: "RefreshConfigs uncaught error", pb: Object.assign(Object.assign({}, RefreshConfigsError.pb), { customMessage: `Error: ${e}` }) }));
            useIndexedDb = false;
        }
        return configsRefreshResponse;
    });
}
/**
 * Write configMap to indexedDb.
 */
function initializeIndexedDb() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!useIndexedDb) {
            return;
        }
        const initializeIndexedDbPromise = "initializeIndexedDb";
        if (configTreePromiseMap.has(initializeIndexedDbPromise)) {
            logger.info("WebWorker.initializeIndexedDb: promise was already resolved.");
            return configTreePromiseMap.get(initializeIndexedDbPromise);
        }
        const initializeIdbPromise = new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            try {
                yield indexedDb.initializeIndexedDb(1, "Configurations_12", "wwConfigs");
                logger.info("WebWorker.initializeIndexedDb: completed");
                resolve();
            }
            catch (e) {
                logger.error(Object.assign(Object.assign({}, IdbConfigInitFailed), { message: "Failed to init configs IndexedDB", pb: Object.assign(Object.assign({}, IdbConfigInitFailed.pb), { customMessage: `Error: ${e}` }) }));
                useIndexedDb = false;
                reject(e);
            }
        }));
        configTreePromiseMap.set(initializeIndexedDbPromise, initializeIdbPromise);
    });
}
/**
 * Clear configs from IndexedDb.
 * @param: configIndexHref to delete config.
 */
export function clearConfigCache(configIndexHref) {
    if (!useIndexedDb) {
        return;
    }
    // when config index href exists
    if (configIndexHref
        // Configs are not written to indexedDb yet.
        && !writeConfigsToIndexedDbCalled
        // ConfigMap has this config index href
        && configMap.has(configIndexHref)) {
        configMap.delete(configIndexHref);
        logger.info(`WebWorker.clearConfigCache: removed config ${configIndexHref} from configMap.`);
        return;
    }
    // If confixIndexHref is undefined or writeConfigsToIndexedDbCalled was true, clear configs in indexedDb.
    clearIndexedDb();
}
/**
 * Clear all configs from IndexedDb.
 */
function clearIndexedDb() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!useIndexedDb) {
            return;
        }
        try {
            yield initializeIndexedDb();
            yield indexedDb.clearObjects();
            logger.info("WebWorker.clearIndexedDb: done.");
        }
        catch (e) {
            logger.error(Object.assign(Object.assign({}, IdbConfigClearFailed), { message: "Failed to clear configs indexedDb", pb: Object.assign(Object.assign({}, IdbConfigClearFailed.pb), { customMessage: `Error: ${e}` }) }));
            // Once we get an exception using IndexedDb, stop trying to use it.
            useIndexedDb = false;
        }
    });
}
/**
 * Function to check config is stale.
 * @returns true if stale else false.
 */
function isConfigStale(storageTimestamp) {
    if (!storageTimestamp) {
        return true;
    }
    // 259200000 = 3 days in milliseconds
    if ((stalenessCheckTimestamp - storageTimestamp) > 259200000) {
        return true;
    }
    return false;
}
/**
 * This is a recursive function to parse and download config tree.
 * @param configIndexHref: Relative Url of config Index href
 */
function getConfigTree(configIndexHref) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!stringHasData(configIndexHref)) {
            logger.error(Object.assign(Object.assign({}, HrefNotValid), { message: "ConfigIndexHref is invalid" }));
            return;
        }
        let mergedConfigsMap;
        try {
            // Force to load config from network.
            mergedConfigsMap = yield getConfigFromNetwork(configIndexHref);
        }
        catch (e) {
            logger.error(Object.assign(Object.assign({}, GetConfigTreeError), { message: "getConfigTree threw error", pb: Object.assign(Object.assign({}, GetConfigTreeError.pb), { customMessage: `${configIndexHref}. Error: ${e}` }) }));
            return;
        }
        if (!mergedConfigsMap || mergedConfigsMap.size === 0) {
            logger.error(Object.assign(Object.assign({}, GetConfigTreeEmpty), { message: "Didn't return config", pb: Object.assign(Object.assign({}, GetConfigTreeEmpty.pb), { customMessage: configIndexHref }) }));
            return;
        }
        const promises = [];
        mergedConfigsMap.forEach((resolvedConfig, configIndexKey) => {
            visitedHrefs[configIndexKey] = true;
            if (!resolvedConfig) {
                logger.error(Object.assign(Object.assign({}, GetConfigTreeNull), { message: "Returned null config", pb: Object.assign(Object.assign({}, GetConfigTreeNull.pb), { customMessage: configIndexKey }) }));
            }
            else {
                checkForExperienceConfigs(experienceConfigsNeeded, resolvedConfig);
                const configIndexHrefSet = getChildrenConfigIndexes(resolvedConfig);
                if (!configIndexHrefSet || configIndexHrefSet.size === 0) {
                    // This is a leaf node and no more child configs are linked.
                    return;
                }
                else {
                    for (const configIndexDocId of configIndexHrefSet) {
                        if (stringHasData(configIndexDocId)
                            && !visitedHrefs[configIndexDocId]
                            && !mergedConfigsMap.has(configIndexDocId)) {
                            // Recursive call to traverse the tree.
                            promises.push(getConfigTree(configIndexDocId));
                        }
                    }
                }
            }
        });
        yield Promise.all(promises);
    });
}
/**
 * This function accepts config properties and returns href of config idnexs present in the properties.
 * IMPORTANT: When change is made in this function, similar change is required in CRS to get max perf benefits.
 * @param resolvedConfig: Experience resolved properties
 * @returns Array of config index found in the resolved config.
 */
export function getChildrenConfigIndexes(resolvedConfig) {
    const config = resolvedConfig.properties;
    if (!config) {
        return;
    }
    const configIndexHrefSet = new Set();
    try {
        // First look for anything that has an "href" property to see if it's a reference to a config index
        addConfigIndiciesInObject(config, configIndexHrefSet);
        // Get from dynamic PivotsNav
        const dynamicPivots = config.dynamicPivots;
        if (dynamicPivots) {
            addUniqueConfigIndexHrefInArray(dynamicPivots.configIndexHref, configIndexHrefSet);
        }
        // Get from static PivotsNav
        addConfigIndexHrefsFromPivots(config.staticPivots, configIndexHrefSet);
        // Get from pivots in Layout data connector
        addConfigIndexHrefsFromPivots(config.pivots, configIndexHrefSet);
        // Get from xfeedConfigIndexHref
        if (config.xfeedConfigIndexHref) {
            addUniqueConfigIndexHrefInArray(config.xfeedConfigIndexHref, configIndexHrefSet);
        }
    }
    catch (error) {
        // No action!
        logger.info(`WebWorker.getChildrenConfigIndexes config failed for: ${resolvedConfig._id}`);
    }
    return configIndexHrefSet;
}
/**
 * Iterates through a list of pivots and adds any config index hrefs to the set of config index hrefs
 * @param pivots List of pivots
 * @param configIndexHrefSet Set of config index hrefs
 */
function addConfigIndexHrefsFromPivots(pivots, configIndexHrefSet) {
    if (!pivots || !pivots.length) {
        return;
    }
    pivots.forEach(pivot => {
        if (pivot && pivot.configIndexHref) {
            addUniqueConfigIndexHrefInArray(pivot.configIndexHref, configIndexHrefSet);
        }
    });
}
/**
 * Adds all config indicies found in the object to the given set, that are found under "href" properties.
 * @param obj The object to search in.
 * @param indicies The set of indicies to add to.
 */
function addConfigIndiciesInObject(obj, indicies) {
    const href = obj.href;
    if (typeof (href) === "string" && href.indexOf("cms/api/amp/experienceConfigIndex/") === 0 && !indicies.has(href)) {
        indicies.add(href);
    }
    for (const value of Object.values(obj)) {
        if (typeof (value) === "object" && value) {
            addConfigIndiciesInObject(value, indicies);
        }
    }
}
/**
 * Helper function to add unique keys in the config tree download process.
 * @param configIndexHref: configIndexHref
 * @param configIndexHrefArray: array of config index href.
 */
function addUniqueConfigIndexHrefInArray(configIndexHref, configIndexHrefSet) {
    if (configIndexHref && !configIndexHrefSet.has(configIndexHref)) {
        configIndexHrefSet.add(configIndexHref);
    }
}
/**
 * Merges config objects.
 * @private
 * @param {ExperienceConfigWrapper[]} configs - an array of config documents
 * @param {string} experienceType - experienceType
 * @return ExperienceConfig - returns merged config based on a set of rules
 */
function mergeConfig(configs) {
    if (!configs || configs.length < 1) {
        return null;
    }
    const result = configs[0];
    // skip the 0th item as it is assigned to result above, only merge properties section
    for (let count = 1; count < configs.length; count++) {
        result.properties = mergeWith(result.properties, configs[count].properties, customizer);
    }
    return result;
}
/**
 * matches targeting scope and returns the config urls for the same.
 * @private
 * @param {IExperienceConfigIndex} configIndex - an array of config documents
 * @return string[] - returns urls for configurations for which the scope matched
 */
function resolveConfigIndex(configIndex) {
    if (!configIndex) {
        return null;
    }
    const configPaths = [];
    configIndex.configs.forEach(experienceRef => {
        if (targetScopeMatched(experienceRef)) {
            configPaths.push(experienceRef.href);
        }
    });
    // if override config is provided, then we add that to the end of list
    // so props provided in override config will add/update existing properties.
    // This is similar to CMS override settings but scoped to experience level.
    if (configIndex.overrideConfig && stringHasData(configIndex.overrideConfig.href)) {
        configPaths.push(configIndex.overrideConfig.href);
    }
    return configPaths;
}
/**
 * basic version of target scope that only checks market currently.
 * @private
 * @param {IExperienceConfigReference} experienceRef - experience config reference that has targetscope as child property
 * @return boolean - true if current target scope matches with config target scope, otherwise false
 */
function targetScopeMatched(experienceRef) {
    const targetScope = experienceRef.targetScope;
    // if target scope is undefined for a config entry, we treat that as default config and apply it
    if (!targetScope) {
        return true;
    }
    return (audienceModeMatched(targetScope)
        && browserTypeMatched(targetScope)
        && deviceTypeMatched(targetScope)
        && domainMatched(targetScope)
        && experimentMatched(targetScope)
        && marketMatched(targetScope)
        && osMatched(targetScope)
        && pageTypeMatched(targetScope)
        && platformMatched(targetScope));
}
/**
 * function to check if current audienceMode type matches target audienceMode type
 * @private
 * @param {PageRequestTargetScope} targetScope - experience config reference that has targetscope as child property
 * @return boolean - true if current target scope audienceMode matches with config target scope audienceMode, otherwise false
 */
function audienceModeMatched(targetScope) {
    if (!targetScope.audienceMode || currentTargetScope.audienceMode === targetScope.audienceMode) {
        return true;
    }
    return false;
}
/**
 * function to check if current browser type matches target browser type
 * @private
 * @param {PageRequestTargetScope} targetScope - experience config reference that has targetscope as child property
 * @return boolean - true if current target scope browser type matches with config target scope browser type, otherwise false
 */
function browserTypeMatched(targetScope) {
    if (!targetScope.browser || !targetScope.browser.browserType) {
        return true;
    }
    if (currentTargetScope.browser && currentTargetScope.browser.browserType === targetScope.browser.browserType &&
        (!targetScope.browser.version || currentTargetScope.browser.version === targetScope.browser.version)) {
        return true;
    }
    return false;
}
/**
 * function to check if current device type matches target device type
 * @private
 * @param {PageRequestTargetScope} targetScope - experience config reference that has targetscope as child property
 * @return boolean - true if current target scope device type matches with config target scope device type, otherwise false
 */
function deviceTypeMatched(targetScope) {
    if (!targetScope.deviceFormFactor || currentTargetScope.deviceFormFactor === targetScope.deviceFormFactor) {
        return true;
    }
    return false;
}
/**
 * Check if current domain matches target domain
 * @param targetScope - experience config reference that has targetscope as child property
 * @return Whether current target scope domain matches with config target scope domain
 */
function domainMatched(targetScope) {
    return !targetScope.domain || currentTargetScope.domain === targetScope.domain;
}
/**
 * function to check if current experiment id matches with the requested scope experiment id.
 * @private
 * @param {PageRequestTargetScope} targetScope - experience config reference that has targetscope as child property
 * @return boolean - true if current target scope experiment matches with config target scope experiment, otherwise false
 */
function experimentMatched(targetScope) {
    currentTargetScope.pageExperiments = currentTargetScope.pageExperiments || [];
    if (!isString(targetScope.experimentId) || isEmpty(targetScope.experimentId)
        || currentTargetScope.pageExperiments.indexOf(targetScope.experimentId) >= 0) {
        return true;
    }
    return false;
}
/**
 * Compare the strings case insensitive. Undefined or null is treated as empty string.
 * @private
 * @param {string} lhs The string to compare.
 * @param {string} rhs The string to compare against.
 * @return {number} - true if the strings equal case insensitive.
 */
function stringsEqualCaseInsensitive(lhs, rhs) {
    return (lhs || "").localeCompare(rhs || "", undefined, { sensitivity: "accent" }) === 0;
}
/**
 * function to check if current market scope match with target scope market.
 * @private
 * @param {PageRequestTargetScope} targetScope - experience config reference that has targetscope as child property
 * @return boolean - true if current target scope market matches with config target scope market, otherwise false
 */
function marketMatched(targetScope) {
    // if locale is not part of target scope, we assume it is applicable for all locale.
    // Separated out this if condition to have clarity about this behavior.
    if (!targetScope.locale) {
        return true;
    }
    const targetLocale = targetScope.locale;
    const currentLocale = currentTargetScope.locale;
    if ((!targetLocale.language || stringsEqualCaseInsensitive(targetLocale.language, currentLocale.language)) &&
        (!targetLocale.market || stringsEqualCaseInsensitive(targetLocale.market, currentLocale.market)) &&
        (!targetLocale.script || stringsEqualCaseInsensitive(targetLocale.script, currentLocale.script))) {
        return true;
    }
    return false;
}
/**
 * function to check if current operating system type matches target operating system type
 * @private
 * @param {PageRequestTargetScope} targetScope - experience config reference that has targetscope as child property
 * @return boolean - true if current target scope operating system type matches with config target scope operating system type, otherwise false
 */
function osMatched(targetScope) {
    return !targetScope.os || currentTargetScope.os === targetScope.os;
}
/**
 * function to check if current pagetype matches target scope
 * @private
 * @param {PageRequestTargetScope} targetScope - experience config reference that has targetscope as child property
 * @return boolean - true if current target scope pagetype matches with config target scope pagetype, otherwise false
 */
function pageTypeMatched(targetScope) {
    if (!targetScope.pageType || currentTargetScope.pageType === targetScope.pageType) {
        return true;
    }
    return false;
}
/**
 * function to check if current scope platform type matches target scope platform type
 * @private
 * @param {PageRequestTargetScope} targetScope - experience config reference that has targetscope as child property
 * @return boolean - true if current target scope platform matches with config target scope platform, otherwise false
 */
function platformMatched(targetScope) {
    if (!targetScope.platform || currentTargetScope.platform === targetScope.platform) {
        return true;
    }
    return false;
}
/**
 * Customizer for merge. We are explcitly overriding the array from taget to source. No array elements from source are preserved.
 * @private
 * @param {any} srcValue - source object to merge. Can be of type any
 * @param {any} objValue - target object to merge. Can be of type any
 * @return target Object when the type is array. Otherwise uses default behavior of merge.
 */
function customizer(srcValue, objValue) {
    if (isArray(srcValue)) {
        return objValue;
    }
}
/**
 * Returns the resolved merged Experience config when passed Experience Config Index.
 * This function downloads the config index, filter based on target scope, download the configs and
 * merge the configs
 * @private
 * @param {string} configIndexHref - Experience Index document relative url.
 * @return Promise<Map<string, ExperienceConfigWrapper>> map of config(s) resolved by service/cdn.
 */
function getConfigFromNetwork(configIndexHref) {
    return __awaiter(this, void 0, void 0, function* () {
        const mergedConfigsMap = new Map();
        if (!currentTargetScope || !configIndexHref || !isString(configIndexHref) || isEmpty(configIndexHref)) {
            logger.error(Object.assign(Object.assign({}, PageScopeInvalid), { message: "Page scope invalid for configIndexHref", pb: Object.assign(Object.assign({}, PageScopeInvalid.pb), { customMessage: configIndexHref || "" }) }));
            return mergedConfigsMap;
        }
        if (enableConfigService) {
            const configServiceRelativeUrl = configIndexHref.replace("cms/api/amp/experienceConfigIndex/", "") + configServiceParameters;
            try {
                // Get mereged config from config service with multi config support.
                if (isMultiConfigRetrievalEnabled) {
                    const crsResponse = yield downloadFile(configServiceBaseUrl, configServiceRelativeUrl, true);
                    if (crsResponse && crsResponse.rootConfigIndexId && crsResponse.configs) {
                        Object.values(crsResponse.configs).forEach((crsMergedConfig) => {
                            if (crsMergedConfig && crsMergedConfig.configIndexHref) {
                                const configIndexKey = crsMergedConfig.configIndexHref;
                                addConfigToConfigMap(configIndexKey, crsMergedConfig);
                                mergedConfigsMap.set(configIndexKey, crsMergedConfig);
                            }
                        });
                        return mergedConfigsMap;
                    }
                }
                else {
                    // Get single merged config from config service
                    const crsConfig = yield downloadFile(configServiceBaseUrl, configServiceRelativeUrl, true);
                    if (crsConfig) {
                        addConfigToConfigMap(configIndexHref, crsConfig);
                        mergedConfigsMap.set(configIndexHref, crsConfig);
                        logger.info(`${configIndexHref} config fetched using CRS without multi config support.`);
                        return mergedConfigsMap;
                    }
                }
            }
            catch (error) {
                logger.error(Object.assign(Object.assign({}, CrsFetchError), { message: "CRS fetch failed due to network error", pb: Object.assign(Object.assign({}, CrsFetchError.pb), { customMessage: `CRS relative Url: ${configServiceRelativeUrl} Error: ${error}` }) }));
            }
        }
        // Get config from CDN when config service is not enabled or it returned null or invalid response.
        const cdnConfig = yield downloadMergeConfig(configIndexHref);
        if (cdnConfig) {
            addConfigToConfigMap(configIndexHref, cdnConfig);
            mergedConfigsMap.set(configIndexHref, cdnConfig);
            // Dev scenario: if config service is not enabled and we got configs from CDN, do not log app error.
            if (enableConfigService) {
                logger.error(Object.assign(Object.assign({}, ConfigsDownloadedByCdn), { message: "Configs served from CDN after CRS fetch failed", pb: Object.assign(Object.assign({}, ConfigsDownloadedByCdn.pb), { customMessage: `Relative Url: ${configIndexHref} CRS Params: ${configServiceParameters}` }) }));
            }
        }
        return mergedConfigsMap;
    });
}
/**
 * Set configMap object with appropriate values.
 * @param configIndexHref example: cms/api/amp/experienceConfigIndex/AfCDeG
 * @param config object of ExperienceConfigWrapper
 */
function addConfigToConfigMap(configIndexHref, config) {
    config.storageTimestamp = stalenessCheckTimestamp;
    config.configIndexHref = configIndexHref;
    // Set configMap for future reference.
    configMap.set(configIndexHref, config);
}
/**
 * Get the config over the network, merge and return.
 * @private
 * @param {string} relativeUrl - Experience Index document relative url.
 * @return Promise<ExperienceConfigWrapper>
 */
function downloadMergeConfig(relativeUrl) {
    return __awaiter(this, void 0, void 0, function* () {
        const experienceIndex = yield downloadFile(cdnConfigBaseUrl, relativeUrl, false);
        const configPaths = resolveConfigIndex(experienceIndex);
        const configObjects = yield downloadExperienceConfig(cdnConfigBaseUrl, configPaths);
        const config = mergeConfig(configObjects);
        config.experienceType = experienceIndex.experienceType;
        return config;
    });
}
/**
 * Returns true if cached config and network fetched config properties are exactly same else false.
 * @private
 * @param {ExperienceConfigWrapper} cachedConfig - config fetched from in memory object.
 * @param {ExperienceConfigWrapper} networkConfig - config fetched over the network.
 * @return boolean
 */
export function areConfigsEqual(cachedConfig, networkConfig) {
    if (!cachedConfig || !networkConfig) {
        return false;
    }
    // Performs a deep comparison between two configs. Should only compare properties section
    return isEqual(cachedConfig.properties, networkConfig.properties);
}
/**
 * Given an array of config urls, this function downloads them in parallel and returns a promise.
 * @private
 * @param {string} baseConfigUrl - base config url depends on implementation
 * @param {string[]} hrefs - Array of configuration relative urls
 * @return Promise<ExperienceConfig[]>
 */
function downloadExperienceConfig(baseConfigUrl, hrefs) {
    return __awaiter(this, void 0, void 0, function* () {
        const promiseArray = [];
        hrefs.forEach(href => {
            promiseArray.push(downloadFile(baseConfigUrl, href, false));
        });
        return yield Promise.all(promiseArray);
    });
}
/**
 * downloads file from CMS given the relative url
 * @private
 * @param {string} relativeUrl - relative url of the document
 * @param {boolean} isCrsFetch - Is this CRS fetch
 * @return Promise<T> returns generic Type object wrapped in Promise
 */
function downloadFile(baseConfigUrl, relativeUrl, isCrsFetch) {
    return __awaiter(this, void 0, void 0, function* () {
        const cachedValue = downloadMap[relativeUrl];
        if (cachedValue) {
            return cachedValue;
        }
        const cachedPromise = donwloadPromiseMap[relativeUrl];
        if (cachedPromise) {
            return cachedPromise;
        }
        const url = new URL(`${relativeUrl}${baseConfigUrl.search}`, baseConfigUrl);
        const fetchUrl = url.toString();
        const resultPromise = fetch(fetchUrl)
            .then(response => {
            if (response.ok) {
                const json = response.json();
                downloadMap[relativeUrl] = json;
                delete donwloadPromiseMap[relativeUrl];
                return json;
            }
            let errorMessage = "";
            if (isCrsFetch) {
                const crsVersion = response.headers.get("X-Crs-BuildVersion") || "";
                const msedgeRef = response.headers.get("x-msedge-ref") || "";
                errorMessage = `msedge ref: ${msedgeRef} Status: ${response.status}, Status Text: ${response.statusText}, Url: ${fetchUrl} and CRS Version: ${crsVersion}`;
                logger.error(Object.assign(Object.assign({}, ConfigCrsFetchNon200Response), { message: "CRS sent non-200 response", pb: Object.assign(Object.assign({}, ConfigCrsFetchNon200Response.pb), { customMessage: errorMessage }) }));
            }
            else {
                errorMessage = `Status: ${response.status}, Status Text: ${response.statusText}, Url: ${fetchUrl}`;
                logger.error(Object.assign(Object.assign({}, ConfigCdnFetchNon200Response), { message: "Cdn config fetch received non-200 response", pb: Object.assign(Object.assign({}, ConfigCdnFetchNon200Response.pb), { customMessage: errorMessage }) }));
            }
            return Promise.resolve();
        });
        donwloadPromiseMap[relativeUrl] = resultPromise;
        return resultPromise;
    });
}
/**
 * Function checks if ConfigService is enabled.
 * @returns true if supported else false.
 */
export function useConfigService() {
    const param = environment.getQueryParameter("enableConfigService");
    if (param) {
        return param.toLocaleLowerCase() === "true";
    }
    // PCS flag to turn on/off ConfigService.
    // When enableConfigService = false, it gets configs from CDN and merge at client side.
    return environment.enableConfigService;
}
/**
 * Function that returns maxDepth if exists in query string else default value.
 * This is the max depth of the config tree.
 * @returns Depth of config in tree.
 */
function getConfigRetrievalMaxDepth() {
    const param = environment.getQueryParameter("maxDepth");
    if (param) {
        return param;
    }
    // PCS flag for maxDepth
    if (environment.featureFlags
        && environment.featureFlags.crsMaxDepth) {
        return environment.featureFlags.crsMaxDepth;
    }
    // Config service to use this default to parse depth of config tree.
    return "10";
}
/**
 * Function checks if url has any dev only query string params
 * @returns true if exist else false.
 */
function containsDevOnlyQueryParameters() {
    const item = environment.getQueryParameter("item");
    if (item) {
        return true;
    }
    const segments = environment.getQueryParameter("pcsonly");
    if (segments) {
        return true;
    }
    return false;
}
/**
 * For testing purpose to access what has been set for configServiceParameters
 */
export function getConfigServiceParameters() {
    return configServiceParameters;
}
/**
 * Checks if the resolved config is of the desired experience type, and updates the experienceconfigmap
 * @param resolvedConfig Resolved Config
 */
export function checkForExperienceConfigs(experienceTypes, resolvedConfig) {
    for (const experienceType of experienceTypes) {
        if (!experienceConfigMap.has(experienceType)
            && resolvedConfig
            && resolvedConfig.experienceType
            && resolvedConfig.experienceType.toLowerCase() === experienceType) {
            if (experienceType === ExperienceTypes.River) {
                if (resolvedConfig.properties.isMyFeed && !experienceConfigMap.has(RiverConfigType.MyFeed)) {
                    experienceConfigMap.set(RiverConfigType.MyFeed, resolvedConfig);
                    continue;
                }
                const stripeTopStories = get(resolvedConfig, "properties.templateCategoryFiltersMap.myFeed");
                if (stripeTopStories && !experienceConfigMap.has(RiverConfigType.StripeMyFeed)) {
                    experienceConfigMap.set(RiverConfigType.StripeMyFeed, resolvedConfig);
                }
            }
            else {
                experienceConfigMap.set(experienceType, resolvedConfig);
            }
        }
    }
}
/**
 * Add experience configs from Git to subscribable map.
 */
function addConfigToExperienceConfigMap() {
    if (!experienceGitConfigMap) {
        return;
    }
    for (const experienceType of experienceConfigsNeeded) {
        // if we've already found the type, continue with next type
        if (experienceConfigMap.has(experienceType)) {
            continue;
        }
        const gitConfigs = experienceGitConfigMap.get(experienceType);
        if (!gitConfigs) {
            continue;
        }
        for (const gitConfig of gitConfigs) {
            if (!gitConfig || !gitConfig.properties) {
                return;
            }
            const gitResolvedConfig = {
                properties: gitConfig.properties,
                experienceType,
                _name: experienceType,
                typeVersion: "Git"
            };
            if (experienceType === ExperienceTypes.River) {
                // There can definitely be more than one river. So use the .isMyFeed property to distinguish
                // the main/default feed instance. Once we found it continue processing in case we also need to
                // find the stripe myfeed river
                if (gitResolvedConfig.properties.isMyFeed && !experienceConfigMap.has(RiverConfigType.MyFeed)) {
                    experienceConfigMap.set(RiverConfigType.MyFeed, gitResolvedConfig);
                    continue;
                }
                // Identify the stripe myfeed river
                const stripeTopStories = get(gitResolvedConfig.properties, "templateCategoryFiltersMap.myFeed");
                if (stripeTopStories && !experienceConfigMap.has(RiverConfigType.StripeMyFeed)) {
                    experienceConfigMap.set(RiverConfigType.StripeMyFeed, gitResolvedConfig);
                }
            }
            else {
                // HACK: if there are multiple configs of the same type, this map will only contain one of them.
                //  I guess we must not care maybe because most of the config type subscriptions we have are usually
                //  singletons on a page?? Also note that we can break out of the loop since we only need one instance.
                experienceConfigMap.set(experienceType, gitResolvedConfig);
                break;
            }
        }
    }
}
/**
     * Returns the config for the given experience type
     * @param experienceType Experience Type requested
     * @returns merged config for experience type
     */
export function getExperienceTypeConfig(experienceType) {
    return experienceConfigMap.get(experienceType);
}
/**
     * Returns config from configMap.
     * @returns merged config from cache.
     */
export function getConfigFromMap(configIndexHref) {
    return configMap.get(configIndexHref) || undefined;
}
/**
     * Function to clear internal state map
     * @public
     * @void
     */
export function clearInternalState() {
    configMap.clear();
    configTreePromiseMap.clear();
    downloadMap.clear();
    donwloadPromiseMap.clear();
}
//# sourceMappingURL=WebWorkerResolver.js.map